import { BookOpen, Calendar, GraduationCap, MailOpen, User } from 'lucide-react';
import { Link } from 'react-router';
import { parseAsStringLiteral, useQueryState } from 'nuqs';

import { useGetCourses } from '../query/course';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

export const COURSE_STATUS_VALUES = ['current', 'done'] as const;

const CourseCards = () => {
  const [status, setStatus] = useQueryState(
    'status',
    parseAsStringLiteral(COURSE_STATUS_VALUES).withDefault('current'),
  );

  const { data: courses, isSuccess } = useGetCourses({
    currentStatus: status,
  });

  return (
    <>
      <div className="flex gap-8 mb-8">
        <button
          onClick={() => setStatus('current')}
          className={`pb-3 border-b-2 font-medium ${
            status === 'current'
              ? 'border-gray-900 text-gray-900'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          강의중인 강좌
        </button>
        <button
          onClick={() => setStatus('done')}
          className={`pb-3 border-b-2 font-medium ${
            status === 'done'
              ? 'border-gray-900 text-gray-900'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          과거 강좌
        </button>
      </div>

      {isSuccess && (courses?.data ?? []).length === 0 && (
        <Card className="flex flex-col items-center justify-center py-24 px-4">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
            <BookOpen className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">등록된 강좌가 없습니다</h3>
          <p className="text-gray-500 text-center mb-8 max-w-md">
            아직 참여한 강좌가 없습니다. <br />
            새로운 강좌에 참여해 학습을 시작해보세요.
          </p>
        </Card>
      )}

      {/* 강좌 목록 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {(courses?.data ?? []).map(course => (
          <Card key={course.id} className="bg-white">
            <CardHeader className="pb-4">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold text-gray-900 mb-1">
                    {course.name}
                  </CardTitle>
                  <p className="text-sm text-blue-600 font-medium">{course.subject.name}</p>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <GraduationCap className="h-4 w-4" />
                  <span>
                    {course.school} {course.department}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <User className="h-4 w-4" />
                  <span>{course.professor.name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <MailOpen className="h-4 w-4" />
                  <span>{course.professor.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="h-4 w-4" />
                  <span>
                    {course.year} {course.semester}
                  </span>
                </div>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <div className="flex items-center justify-end mt-2">
                  <Link to={`/course/${course.id}/notice`}>
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                      상세
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
};

export default CourseCards;
